import React from 'react';
import { ShimmeredDetailsList, SelectionMode, IColumn, Label, Checkbox, FontWeights, mergeStyleSets } from '@fluentui/react';
import { LogbookEntry } from '../../Model/LogbookEntry';
import { Rate, User } from '../../services/ApiService';

interface LogbookGridProps {
    entries: LogbookEntry[];
    showAllRides: boolean;
    onCheckboxChange: (item: LogbookEntry, checked: boolean) => Promise<void>;
    theme: any;
    styles: any;
    rates: Rate[];
    users: User[];
}

const fmtKm = (v: number | null | undefined) =>
    v != null && v !== 0 ? `${v.toLocaleString()} km` : '—';

const fmtMoney = (v: number | null | undefined) =>
    v != null ? `${Math.round(v).toLocaleString()} Kč` : '—';

export const LogbookGrid: React.FC<LogbookGridProps> = ({
    entries,
    showAllRides,
    onCheckboxChange,
    theme,
    styles,
    rates,
    users
}) => {
    const rateById = React.useMemo(() => {
        const map = new Map<string, Rate>();
        rates.forEach(r => map.set(r.id, r));
        return map;
    }, [rates]);

    const displayNameByEmail = React.useMemo(() => {
        const map = new Map<string, string>();
        users.forEach(u => { if (u.displayName) map.set(u.email, u.displayName); });
        return map;
    }, [users]);
    const localStyles = mergeStyleSets({
        agreedPriceNote: {
            fontSize: '11px',
            fontWeight: 400,
            marginLeft: 4,
            color: theme.palette.neutralSecondary,
        },
        kmValue: {
            fontVariantNumeric: 'tabular-nums',
            fontSize: '13px',
        },
        purposeBadge: {
            display: 'inline-block',
            fontWeight: 600,
            fontSize: '11px',
            letterSpacing: '0.3px',
            padding: '3px 9px',
            borderRadius: '12px',
            whiteSpace: 'nowrap',
        },
        purposeTaxi: { color: '#ffffff', background: '#1a7f4b', border: '1px solid #15663c' },
        purposeService: { color: '#ffffff', background: '#0f5bab', border: '1px solid #0c4a8a' },
        purposeContract: { color: '#ffffff', background: '#c85000', border: '1px solid #a04000' },
        purposePersonal: { color: '#ffffff', background: '#7b39a8', border: '1px solid #622e87' },
        purposeUnknown: { color: theme.palette.neutralDark, background: theme.palette.neutralLighter, border: `1px solid ${theme.palette.neutralQuaternaryAlt}` },
    });

    const sorted = React.useMemo(
        () => [...entries].sort((a, b) => (b.kilometersState ?? 0) - (a.kilometersState ?? 0)),
        [entries]
    );

    const columns: IColumn[] = [
        {
            key: 'envelope',
            name: 'Obálka',
            fieldName: 'envelope',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontWeight: FontWeights.semibold, color: theme.palette.themePrimary, fontSize: '13px' } }}>
                    {item.envelope}
                </Label>
            ),
        },
        {
            key: 'kilometersState',
            name: 'Stav KM',
            fieldName: 'kilometersState',
            minWidth: 90,
            maxWidth: 110,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{
                    root: {
                        fontWeight: FontWeights.bold,
                        fontSize: '13px',
                        color: theme.palette.neutralDark,
                        fontVariantNumeric: 'tabular-nums',
                    }
                }}>
                    {item.kilometersState?.toLocaleString()} km
                </Label>
            ),
        },
        {
            key: 'timestamp',
            name: 'Datum',
            fieldName: 'timestamp',
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px' } }}>
                    {item.timestamp?.toLocaleDateString("cs-CZ")}
                </Label>
            ),
        },
        {
            key: 'driverid',
            name: 'Řidič',
            fieldName: 'driverId',
            minWidth: 130,
            maxWidth: 180,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontWeight: FontWeights.semibold, fontSize: '13px' } }}>
                    {item.driverId ? (displayNameByEmail.get(item.driverId) ?? item.driverId) : '—'}
                </Label>
            ),
        },
        {
            key: 'carId',
            name: 'Auto',
            fieldName: 'carId',
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{
                    root: {
                        fontWeight: FontWeights.semibold,
                        fontSize: '13px',
                        color: theme.palette.themeDark,
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: theme.palette.themeLighterAlt,
                    }
                }}>
                    {item.carId}
                </Label>
            ),
        },
        {
            key: 'purpose',
            name: 'Účel',
            fieldName: 'purpose',
            minWidth: 120,
            maxWidth: 140,
            isResizable: true,
            onRender: (item: LogbookEntry) => {
                const rateTypeMap: Record<string, { text: string; cls: string }> = {
                    'Taxi': { text: '🚕 TAXI', cls: localStyles.purposeTaxi },
                    'Service': { text: '🔧 Služební', cls: localStyles.purposeService },
                    'Contract': { text: '📄 Smluvní', cls: localStyles.purposeContract },
                    'Personal': { text: '👤 Soukromé', cls: localStyles.purposePersonal },
                };
                const rate = item.purpose ? rateById.get(item.purpose) : undefined;
                const p = rate ? rateTypeMap[rate.rateType] : undefined;
                const label = p?.text ?? (rate?.purpose || item.purpose || '—');
                return (
                    <span className={`${localStyles.purposeBadge} ${p?.cls ?? localStyles.purposeUnknown}`}>
                        {label}
                    </span>
                );
            },
        },
        {
            key: 'kilometersBillable',
            name: 'Placené KM',
            fieldName: 'kilometersBillable',
            minWidth: 90,
            maxWidth: 110,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px', fontVariantNumeric: 'tabular-nums' } }}>
                    {fmtKm(item.kilometersBillable)}
                </Label>
            ),
        },
        {
            key: 'kilometersPersonalWriteoff',
            name: 'Soukromé odpisy',
            fieldName: 'kilometersPersonalWriteoff',
            minWidth: 90,
            maxWidth: 110,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px', color: theme.palette.neutralSecondary, fontVariantNumeric: 'tabular-nums' } }}>
                    {fmtKm(item.kilometersPersonalWriteoff)}
                </Label>
            ),
        },
        {
            key: 'kilometersCompanyWriteoff',
            name: 'Firemní odpisy',
            fieldName: 'kilometersCompanyWriteoff',
            minWidth: 90,
            maxWidth: 110,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px', color: theme.palette.neutralSecondary, fontVariantNumeric: 'tabular-nums' } }}>
                    {fmtKm(item.kilometersCompanyWriteoff)}
                </Label>
            ),
        },
        {
            key: 'kilometersGeneralWriteoff',
            name: 'Marné odpisy',
            fieldName: 'kilometersGeneralWriteoff',
            minWidth: 110,
            maxWidth: 130,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px', color: theme.palette.neutralSecondary, fontVariantNumeric: 'tabular-nums' } }}>
                    {fmtKm(item.kilometersGeneralWriteoff)}
                </Label>
            ),
        },
        {
            key: 'rate',
            name: 'Sazba',
            fieldName: 'rate',
            minWidth: 70,
            maxWidth: 90,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px', fontVariantNumeric: 'tabular-nums' } }}>
                    {item.rate != null ? `${item.rate} Kč/km` : '—'}
                </Label>
            ),
        },
        {
            key: 'depositAmount',
            name: 'Záloha',
            fieldName: 'depositAmount',
            minWidth: 90,
            maxWidth: 110,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px', fontVariantNumeric: 'tabular-nums' } }}>
                    {fmtMoney(item.depositAmount)}
                </Label>
            ),
        },
        {
            key: 'additionalExpenses',
            name: 'Náklady',
            fieldName: 'additionalExpenses',
            minWidth: 90,
            maxWidth: 110,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px', fontVariantNumeric: 'tabular-nums' } }}>
                    {fmtMoney(item.additionalExpenses)}
                </Label>
            ),
        },
        {
            key: 'cardPayments',
            name: 'Platba kartou',
            fieldName: 'cardPayments',
            minWidth: 100,
            maxWidth: 130,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { color: theme.palette.tealDark, fontSize: '13px', fontVariantNumeric: 'tabular-nums' } }}>
                    {fmtMoney(item.cardPayments)}
                </Label>
            ),
        },
        {
            key: 'finalPrice',
            name: 'Tržba',
            fieldName: 'finalPrice',
            minWidth: 110,
            maxWidth: 140,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{
                    root: {
                        fontWeight: FontWeights.bold,
                        fontSize: '14px',
                        color: theme.palette.themePrimary,
                        fontVariantNumeric: 'tabular-nums',
                    }
                }}>
                    {Math.round(item.finalPrice).toLocaleString()} Kč
                </Label>
            ),
        },
        {
            key: 'rowChecked',
            name: 'Stav',
            fieldName: 'rowChecked',
            minWidth: 130,
            maxWidth: 150,
            isResizable: false,
            onRender: (item: LogbookEntry) => (
                <Checkbox
                    checked={item.rowChecked}
                    label={item.rowChecked ? "Zkontrolováno" : "Čeká na kontrolu"}
                    styles={{
                        root: { marginTop: 2 },
                        label: {
                            color: item.rowChecked ? theme.palette.green : theme.palette.neutralSecondary,
                            fontWeight: item.rowChecked ? FontWeights.semibold : FontWeights.regular,
                            fontSize: '12px',
                        }
                    }}
                    onChange={async (ev, checked) => {
                        if (checked !== undefined && checked) {
                            await onCheckboxChange(item, checked);
                        }
                    }}
                />
            ),
        },
    ];

    return (
        <div className={styles.gridContainer}>
            <h3 className={styles.gridHeader}>
                📋 Kniha jízd - {showAllRides ? 'Všechny záznamy' : 'Nezpracované záznamy'}
            </h3>
            <ShimmeredDetailsList
                items={sorted}
                columns={columns}
                compact={false}
                selectionMode={SelectionMode.none}
                styles={{
                    root: {
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        '.ms-DetailsHeader': {
                            background: theme.palette.neutralLighterAlt,
                            borderBottom: `2px solid ${theme.palette.neutralQuaternaryAlt}`,
                        },
                        '.ms-DetailsHeader-cell': {
                            fontSize: '14px',
                            fontWeight: FontWeights.bold,
                            color: theme.palette.neutralDark,
                            padding: '12px 8px',
                        },
                        '.ms-DetailsRow': {
                            borderBottom: `1px solid ${theme.palette.neutralLighter}`,
                            '&:hover': {
                                background: theme.palette.neutralLighterAlt,
                            },
                        },
                        '.ms-DetailsRow-cell': {
                            padding: '12px 8px',
                            fontSize: '13px',
                        },
                        '.ms-DetailsList-contentWrapper': {
                            overflow: 'hidden',
                        }
                    }
                }}
            />
        </div>
    );
};
